import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { getServiceRoleClient as getSupabaseAdmin } from "@/lib/interacciones.server";
import { resolveCurrentUserId } from "@/lib/articulos.server";

const Uuid = z.string().uuid();
const Visitor = z.string().min(8).max(64).optional();

type Owner = { userId: string | null; visitorId: string | null };

async function resolveOwner(visitor?: string | null): Promise<Owner> {
  const userId = await resolveCurrentUserId();
  if (userId) return { userId, visitorId: null };
  const v = (visitor ?? "").trim();
  return { userId: null, visitorId: v ? v : null };
}

function ownerFilter(q: any, owner: Owner) {
  if (owner.userId) return q.eq("user_id", owner.userId);
  if (owner.visitorId) return q.is("user_id", null).eq("visitor_id", owner.visitorId);
  return q.eq("user_id", "00000000-0000-0000-0000-000000000000"); // nada
}

// ─────── Estado del usuario actual para una lista de anuncios ───────
export const getMyInteractions = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        anuncio_ids: z.array(Uuid).max(500),
        visitor_id: Visitor,
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const owner = await resolveOwner(data.visitor_id);
    if (data.anuncio_ids.length === 0) return { items: [], userId: owner.userId };
    const sb = getSupabaseAdmin();
    let q = sb
      .from("anuncio_interacciones")
      .select("anuncio_id, favorito, descartado, vistas, impresiones")
      .in("anuncio_id", data.anuncio_ids);
    q = ownerFilter(q, owner);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return { items: rows ?? [], userId: owner.userId };
  });

// ─────── IDs descartados por el usuario (para filtrar el listado) ───────
export const getDescartadosIds = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z.object({ visitor_id: Visitor }).parse(d ?? {}),
  )
  .handler(async ({ data }) => {
    const owner = await resolveOwner(data.visitor_id);
    if (!owner.userId) return { ids: [] as string[] };
    const sb = getSupabaseAdmin();
    const { data: rows, error } = await sb
      .from("anuncio_interacciones")
      .select("anuncio_id")
      .eq("user_id", owner.userId)
      .eq("descartado", true);
    if (error) throw new Error(error.message);
    return { ids: (rows ?? []).map((r: { anuncio_id: string }) => r.anuncio_id) };
  });

// ─────── Toggle favorito (requiere login) ───────
export const toggleFavorito = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z.object({ anuncio_id: Uuid, value: z.boolean().optional() }).parse(d),
  )
  .handler(async ({ data }) => {
    const userId = await resolveCurrentUserId();
    if (!userId) throw new Error("Debes iniciar sesión");
    const sb = getSupabaseAdmin();
    const { data: existing } = await sb
      .from("anuncio_interacciones")
      .select("id, favorito")
      .eq("user_id", userId)
      .eq("anuncio_id", data.anuncio_id)
      .maybeSingle();
    const next = data.value ?? !(existing?.favorito ?? false);
    if (!existing) {
      const { error } = await sb.from("anuncio_interacciones").insert({
        user_id: userId,
        anuncio_id: data.anuncio_id,
        favorito: next,
        favorito_at: next ? new Date().toISOString() : null,
      });
      if (error) throw new Error(error.message);
    } else {
      const { error } = await sb
        .from("anuncio_interacciones")
        .update({
          favorito: next,
          favorito_at: next ? new Date().toISOString() : null,
        })
        .eq("id", existing.id);
      if (error) throw new Error(error.message);
    }
    return { favorito: next };
  });

// ─────── Toggle descartado (requiere login) ───────
export const toggleDescartado = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z.object({ anuncio_id: Uuid, value: z.boolean().optional() }).parse(d),
  )
  .handler(async ({ data }) => {
    const userId = await resolveCurrentUserId();
    if (!userId) throw new Error("Debes iniciar sesión");
    const sb = getSupabaseAdmin();
    const { data: existing } = await sb
      .from("anuncio_interacciones")
      .select("id, descartado")
      .eq("user_id", userId)
      .eq("anuncio_id", data.anuncio_id)
      .maybeSingle();
    const next = data.value ?? !(existing?.descartado ?? false);
    if (!existing) {
      const { error } = await sb.from("anuncio_interacciones").insert({
        user_id: userId,
        anuncio_id: data.anuncio_id,
        descartado: next,
        descartado_at: next ? new Date().toISOString() : null,
      });
      if (error) throw new Error(error.message);
    } else {
      const { error } = await sb
        .from("anuncio_interacciones")
        .update({
          descartado: next,
          descartado_at: next ? new Date().toISOString() : null,
        })
        .eq("id", existing.id);
      if (error) throw new Error(error.message);
    }
    return { descartado: next };
  });

// ─────── Track impresiones (lote) ───────
export const trackImpresiones = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        anuncio_ids: z.array(Uuid).max(200),
        visitor_id: Visitor,
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    if (data.anuncio_ids.length === 0) return { ok: true, count: 0 };
    const owner = await resolveOwner(data.visitor_id);
    if (!owner.userId && !owner.visitorId) return { ok: false, count: 0 };
    const sb = getSupabaseAdmin();
    const now = new Date().toISOString();

    // Filas existentes
    let q = sb
      .from("anuncio_interacciones")
      .select("id, anuncio_id, impresiones")
      .in("anuncio_id", data.anuncio_ids);
    q = ownerFilter(q, owner);
    const { data: existing, error: selErr } = await q;
    if (selErr) throw new Error(selErr.message);

    const byAnuncio = new Map<string, { id: string; impresiones: number }>(
      (existing ?? []).map((r: any) => [r.anuncio_id as string, { id: r.id, impresiones: r.impresiones ?? 0 }]),
    );

    const toInsert: any[] = [];
    const toUpdate: Array<{ id: string; impresiones: number }> = [];
    for (const aid of data.anuncio_ids) {
      const row = byAnuncio.get(aid);
      if (!row) {
        toInsert.push({
          anuncio_id: aid,
          user_id: owner.userId,
          visitor_id: owner.userId ? null : owner.visitorId,
          impresiones: 1,
          primera_impresion: now,
          ultima_impresion: now,
        });
      } else {
        toUpdate.push({ id: row.id, impresiones: row.impresiones + 1 });
      }
    }

    if (toInsert.length) {
      // Puede haber carrera entre llamadas paralelas que intenten insertar la
      // misma fila (user_id, anuncio_id). Ignoramos duplicados silenciosamente.
      const { error } = await sb
        .from("anuncio_interacciones")
        .insert(toInsert);
      if (error && (error as any).code !== "23505") throw new Error(error.message);
    }

    for (const u of toUpdate) {
      const { error } = await sb
        .from("anuncio_interacciones")
        .update({ impresiones: u.impresiones, ultima_impresion: now })
        .eq("id", u.id);
      if (error) throw new Error(error.message);
    }
    return { ok: true, count: data.anuncio_ids.length };
  });

// ─────── Track vista (detalle) ───────
export const trackVista = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z.object({ anuncio_id: Uuid, visitor_id: Visitor }).parse(d),
  )
  .handler(async ({ data }) => {
    const owner = await resolveOwner(data.visitor_id);
    if (!owner.userId && !owner.visitorId) return { ok: false };
    const sb = getSupabaseAdmin();
    const now = new Date().toISOString();

    let q = sb
      .from("anuncio_interacciones")
      .select("id, vistas")
      .eq("anuncio_id", data.anuncio_id);
    q = ownerFilter(q, owner);
    const { data: existing, error: selErr } = await q.maybeSingle();
    if (selErr) throw new Error(selErr.message);

    if (!existing) {
      const { error } = await sb.from("anuncio_interacciones").insert({
        anuncio_id: data.anuncio_id,
        user_id: owner.userId,
        visitor_id: owner.userId ? null : owner.visitorId,
        vistas: 1,
        primera_vista: now,
        ultima_vista: now,
      });
      if (error) throw new Error(error.message);
      return { ok: true, vistas: 1 };
    } else {
      const next = (existing.vistas ?? 0) + 1;
      const { error } = await sb
        .from("anuncio_interacciones")
        .update({ vistas: next, ultima_vista: now })
        .eq("id", existing.id);
      if (error) throw new Error(error.message);
      return { ok: true, vistas: next };
    }
  });

// ─────── Listado de favoritos (IDs) del usuario ───────
export const listFavoritosIds = createServerFn({ method: "GET" })
  .handler(async () => {
    const userId = await resolveCurrentUserId();
    if (!userId) return { ids: [] as string[] };
    const sb = getSupabaseAdmin();
    const { data: rows, error } = await sb
      .from("anuncio_interacciones")
      .select("anuncio_id, favorito_at")
      .eq("user_id", userId)
      .eq("favorito", true)
      .order("favorito_at", { ascending: false });
    if (error) throw new Error(error.message);
    return { ids: (rows ?? []).map((r: { anuncio_id: string }) => r.anuncio_id) };
  });

// ─────── Listado de descartados (IDs) del usuario ───────
export const listDescartadosIds = createServerFn({ method: "GET" })
  .handler(async () => {
    const userId = await resolveCurrentUserId();
    if (!userId) return { ids: [] as string[] };
    const sb = getSupabaseAdmin();
    const { data: rows, error } = await sb
      .from("anuncio_interacciones")
      .select("anuncio_id, descartado_at")
      .eq("user_id", userId)
      .eq("descartado", true)
      .order("descartado_at", { ascending: false });
    if (error) throw new Error(error.message);
    return { ids: (rows ?? []).map((r: { anuncio_id: string }) => r.anuncio_id) };
  });
